import { action, makeObservable, observable } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { Company, Lender, Loan, TCompanyId, TLenderId, TLoanId } from '/src/lib/models';
import { FilterTags, Nullable } from '/src/lib/types';

export interface FilterStore {
  /**
   * ID of selected Company
   */
  _companyFilterId?: TCompanyId;
  /**
   * Name of selected Company
   */
  _companyFilterName?: string;
  /**
   * Company filter tags
   */
  _companyFilterTags?: FilterTags;
  /**
   * List of Companies to filter by
   */
  _companyList: Company[];
  /**
   * ID of selected Loan
   */
  _loanFilterId?: TLoanId;
  /**
   * Name of selected Loan
   */
  _loanFilterName?: string;
  /**
   * Loan filter tags
   */
  _loanFilterTags?: FilterTags;
  /**
   * List of Loans to filter by
   */
  _loanList: Loan[];
  /**
   * ID of selected Lender
   */
  _lenderFilterId?: TLenderId;
  /**
   * Name of selected Lender
   */
  _lenderFilterName?: string;
  /**
   * Lender filter tags
   */
  _lenderFilterTags?: FilterTags;
  /**
   * List of Lenders to filter by
   */
  _lenderList: Lender[];
}

export class FilterStore extends BaseStore {
  _companyFilterId?: TCompanyId = undefined;
  _companyFilterName?: string = undefined;
  _companyFilterTags?: FilterTags = undefined;
  _companyList: Company[] = [];

  _loanFilterId?: TLoanId = undefined;
  _loanFilterName?: string = undefined;
  _loanFilterTags?: FilterTags = undefined;
  _loanList: Loan[] = [];

  _lenderFilterId?: TLenderId = undefined;
  _lenderFilterName?: string = undefined;
  _lenderFilterTags?: FilterTags = undefined;
  _lenderList: Lender[] = [];

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      _companyFilterId: observable,
      _companyFilterName: observable,
      _companyFilterTags: observable,
      _companyList: observable,
      _loanFilterId: observable,
      _loanFilterName: observable,
      _loanFilterTags: observable,
      _loanList: observable,
      _lenderFilterId: observable,
      _lenderFilterName: observable,
      _lenderFilterTags: observable,
      _lenderList: observable,
      batchSet: action,
      companySet: action,
      companyListSet: action,
      companyListUpdateLenders: action,
      loanSet: action,
      loanListSet: action,
      lenderSet: action,
      lenderListSet: action,
    });
  }

  /**
   * Returns the current Company, Loan, and Lender filters.
   */
  batchGet = () => ({
    company: this._companyFilterId
      ? {
          _id: this._companyFilterId,
          name: this._companyFilterName,
          tags: this._companyFilterTags,
        }
      : undefined,
    loan: this._loanFilterId
      ? {
          _id: this._loanFilterId,
          name: this._loanFilterName,
          tags: this._loanFilterTags,
        }
      : undefined,
    lender: this._lenderFilterId
      ? {
          _id: this._lenderFilterId,
          name: this._lenderFilterName,
          tags: this._lenderFilterTags,
        }
      : undefined,
  });

  /**
   * Sets the Company, Loan, and/or Lender filters all at once.
   *
   * @param companyFilter Company to filter by
   * @param loanFilter Loan to filter by
   * @param lenderFilter Lender to filter by
   */
  batchSet = (companyFilter?: Company, loanFilter?: Loan, lenderFilter?: Company) => {
    this._companyFilterId = companyFilter?._id;
    this._companyFilterName = companyFilter?.name;
    this._companyFilterTags = companyFilter?.tags;

    this._loanFilterId = loanFilter?._id;
    this._loanFilterName = loanFilter?.name;
    this._loanFilterTags = loanFilter?.tags;

    this._lenderFilterId = lenderFilter?._id;
    this._lenderFilterName = lenderFilter?.name;
    this._lenderFilterTags = lenderFilter?.tags;
  };

  /**
   * Returns the current Company filter.
   */
  companyGet = (): Partial<Company> | undefined => {
    return this._companyFilterId
      ? {
          _id: this._companyFilterId,
          name: this._companyFilterName,
          tags: this._companyFilterTags,
        }
      : undefined;
  };

  /**
   * Sets the current Company filter.
   *
   * @param filter - Company to begin filtering by
   */
  companySet = (filter?: Company) => {
    this._companyFilterId = filter?._id;
    this._companyFilterName = filter?.name;
    this._companyFilterTags = filter?.tags;
  };

  /**
   * Returns a list of Companies with the selected fields.
   *
   * @param fields - Optional array of Company data model props to include with each list item. If undefined, the entire Company object is used.
   */
  companyListGet = (fields?: string[]): Company[] => {
    if (fields) {
      return this._companyList.map((co) => {
        const retObj: Partial<Company> = {};
        fields.forEach((f) => {
          (retObj as any)[f] = (co as any)[f];
        });
        return retObj;
      }) as Company[];
    } else {
      return this._companyList;
    }
  };

  /**
   * Sets the list of Companies.
   *
   * @param list - Array of Companies
   */
  companyListSet = (list?: Partial<Company>[]) => {
    if (list !== undefined) {
      list.sort((a, b) => {
        if (a.name && b.name) {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
        }
        return 0;
      });
      this._companyList = list as Company[];
    }
  };

  /**
   * Update Lender tags for Company list.
   */
  companyListUpdateLenders = () => {
    this._companyList.forEach((company: Company) => {
      this._lenderList.forEach((lender: Lender) => {
        const borrowers = lender.tags?.companies;
        borrowers?.forEach(function (borrower) {
          if (lender._id && borrower === company._id) {
            company.tags?.lenders.push(lender._id);
          }
        });
      });
    });
  };

  /**
   * Returns the current Loan filter.
   */
  loanGet = (): Partial<Loan> | undefined => {
    return this._loanFilterId
      ? {
          _id: this._loanFilterId,
          name: this._loanFilterName,
          tags: this._loanFilterTags,
        }
      : undefined;
  };

  /**
   * Sets the current Loan filter.
   *
   * @param filter - Loan to begin filtering by
   */
  loanSet = (filter?: Loan) => {
    this._loanFilterId = filter?._id;
    this._loanFilterName = filter?.name;
    this._loanFilterTags = filter?.tags;
  };

  /**
   * Returns a list of Loans with the selected fields.
   *
   * @param fields - Optional array of Loan data model props to include with each list item. If undefined, the entire Loan object is used.
   */
  loanListGet = (fields?: string[]): Loan[] => {
    if (fields) {
      return this._loanList.map((co) => {
        const retObj: Partial<Loan> = {};
        fields.forEach((f) => {
          (retObj as any)[f] = (co as any)[f];
        });
        return retObj;
      }) as Loan[];
    } else {
      return this._loanList;
    }
  };

  /**
   * Sets the list of Loans.
   *
   * @param list - Array of Loans
   */
  loanListSet = (list?: Partial<Loan>[]) => {
    if (list !== undefined) {
      list.sort((a, b) => {
        if (a.name && b.name) {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
        }
        return 0;
      });
      this._loanList = list as Loan[];
    }
  };

  /**
   * Returns the current Lender filter.
   */
  lenderGet = () => {
    return this._lenderFilterId
      ? {
          _id: this._lenderFilterId,
          name: this._lenderFilterName,
          tags: this._lenderFilterTags,
        }
      : undefined;
  };

  /**
   * Sets the current Lender filter.
   *
   * @param filter - Lender to begin filtering by
   */
  lenderSet = (filter?: Lender) => {
    this._lenderFilterId = filter?._id;
    this._lenderFilterName = filter?.name;
    this._lenderFilterTags = filter?.tags;
  };

  /**
   * Returns a list of Lenders with the selected fields.
   *
   * @param fields - Optional array of Lender data model props to include with each list item. If undefined, the entire Lender object is used.
   */
  lenderListGet = (lenderSearch?: Nullable<string>, fields?: string[]): Lender | Lender[] | undefined => {
    if (!lenderSearch) {
      if (fields) {
        return this._lenderList.map(function (co) {
          const retObj: Partial<Lender> = {};
          fields.forEach((f) => {
            (retObj as any)[f] = (co as any)[f];
          });
          return retObj;
        }) as Lender[];
      } else {
        return this._lenderList;
      }
    } else {
      if (this._lenderList.length) {
        for (const lender in this._lenderList) {
          if (this._lenderList[lender].name === lenderSearch) {
            return this._lenderList[lender];
          } else if (this._lenderList[lender]._id === lenderSearch) {
            return this._lenderList[lender];
          }
        }
      }
    }
  };

  /**
   * Sets the list of Lenders.
   *
   * @param list - Array of Lenders
   */
  lenderListSet = (list?: Partial<Lender>[]) => {
    if (list !== undefined) {
      list.sort((a, b) => {
        if (a.name && b.name) {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
        }
        return 0;
      });
      this._lenderList = list as Lender[];
    }
  };

  /**
   * Resets all filters.
   */
  reset = () => {
    this.companySet({
      _id: undefined,
      name: undefined,
      tags: undefined,
    } as Partial<Company> as Company);

    this.loanSet({
      _id: undefined,
      name: undefined,
      tags: undefined,
    } as Partial<Loan> as Loan);

    this.lenderSet({
      _id: undefined,
      name: undefined,
      tags: undefined,
    } as Partial<Lender> as Lender);
  };
}
